import React, { useMemo, useRef, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import ReactApexChart from 'react-apexcharts'

import { httpOk, GET } from 'helpers/api'
import { formatMoney, formatDate } from 'helpers/helpers'
import { StandardFields, FilterSegment } from 'pages/main/analytics/parts/AnalyticsFilterBar'
import useAnalyticsOptions from 'pages/main/analytics/parts/useAnalyticsOptions'
import { buildAnalyticsUrl } from 'pages/main/analytics/parts/analyticsDates'
import ChainFilterBar, { useChainFilters, FilterField, FilterSelect, ExcelButton } from '../parts/ChainFilterBar'
import { downloadExcel, periodText } from '../parts/chainExcel'
import {
	money, percent, columnStats, heatStyle, intensityStyle, chainColor,
	FillCell, useSort, SortTh, ChainKpi, HeatLegend, ChainEmpty,
} from '../parts/chainUi'

// Ось по умолчанию — до первого ответа сервера (он пришлёт ось от начала бизнес-дня)
const DEFAULT_AXIS = Array.from({ length: 24 }, (item, index) => (index + 6) % 24)
const WEEK_DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
const CHANNEL_COLORS = { hall: '#5b73e8', takeaway: '#f59f00', delivery: '#12b886' }

function formatHour(hour) {
	return `${String(hour).padStart(2, '0')}:00`
}

function dayLabel(value) {
	return value ? value.split('-').reverse().join('.') : ''
}

// Короткая сумма для тесной ячейки: 1,2M / 350K
function shortMoney(value) {
	const number = Number(value || 0)
	if (Math.abs(number) >= 1000000) return `${(number / 1000000).toFixed(1).replace('.', ',')}M`
	if (Math.abs(number) >= 1000) return `${Math.round(number / 1000)}K`
	return formatMoney(Math.round(number))
}

function Hours() {
	const { t } = useTranslation()
	const [data, setData] = useState(null)
	const [tab, setTab] = useState('heat')
	const [metric, setMetric] = useState('cheques')
	const [chartMode, setChartMode] = useState('absolute')
	const [details, setDetails] = useState(null)

	const filters = useChainFilters({ order_type_ids: [], source_ids: [], start_hour: null, end_hour: null, week_days: [] }, searchWithFilter)
	const { draft, patch, applied } = filters
	const options = useAnalyticsOptions(['sources'])

	async function searchWithFilter(filter) {
		const response = await GET(buildAnalyticsUrl('/chain/report/hours', filter), {}, { loader: false })
		if (httpOk(response)) setData(response.data)
	}

	const axis = data?.axis || DEFAULT_AXIS
	const hourOptions = axis.map(hour => ({ id: hour, name: formatHour(hour) }))
	const hourToOptions = axis.map(hour => ({ id: hour, name: `${String(hour).padStart(2, '0')}:59` }))
	const weekDayOptions = WEEK_DAY_KEYS.map((key, index) => ({ id: index + 1, name: t(key) }))
	const weekDayName = (day) => t(WEEK_DAY_KEYS[day - 1])
	const orderTypeName = (id) => ({ 1: t('hall'), 2: t('takeaway'), 3: t('delivery') })[id] || '—'

	const isRevenue = metric === 'revenue'
	const metricLabel = isRevenue ? t('revenue') : t('cheques')
	const formatMetric = (value) => isRevenue ? money(value) : formatMoney(Math.round(Number(value || 0) * 10) / 10)
	const formatShort = (value) => {
		if (!value) return ''
		return isRevenue ? shortMoney(value) : formatMoney(Math.round(Number(value) * 10) / 10)
	}

	const hours = useMemo(() => data?.hours || [], [data])
	const byHour = useMemo(() => data?.by_hour || [], [data])
	const totals = data?.totals || {}
	const hasData = Number(totals.cheques || 0) > 0

	// ---------- Тепловая карта день × час ----------
	const heat = useMemo(() => {
		const map = {}
		let max = 0
		;(data?.heat || []).forEach(cell => {
			const value = isRevenue ? Number(cell.revenue) : Number(cell.cheques)
			map[`${cell.date}|${cell.hour}`] = value
			if (value > max) max = value
		})
		return { map, max }
	}, [data, isRevenue])

	// ---------- Часы: пик и тихий час по выбранной метрике ----------
	const metricKey = isRevenue ? 'revenue' : 'cheques'
	const peak = useMemo(() => {
		const active = byHour.filter(row => row.cheques > 0)
		if (active.length === 0) return { top: null, low: null }
		const sortedRows = [...active].sort((a, b) => b[metricKey] - a[metricKey])
		return { top: sortedRows[0], low: sortedRows.length > 1 ? sortedRows[sortedRows.length - 1] : null }
	}, [byHour, metricKey])

	const busiestWeekday = useMemo(() => {
		const list = data?.weekdays || []
		if (list.length < 2) return null
		const key = isRevenue ? 'avg_revenue' : 'avg_cheques'
		return [...list].sort((a, b) => b[key] - a[key])[0]
	}, [data, isRevenue])

	const hourStats = useMemo(() => ({
		avg_cheque: columnStats(byHour.filter(row => row.cheques > 0), 'avg_cheque'),
	}), [byHour])
	const maxHourValue = useMemo(() => Math.max(0, ...byHour.map(row => Number(row[metricKey] || 0))), [byHour, metricKey])

	// ---------- Филиалы × часы ----------
	const branches = useMemo(() => data?.branches || [], [data])
	const readBranchSort = (row, key) => {
		if (key.startsWith('h:')) {
			const cell = row.hours?.[key.slice(2)]
			return cell ? Number(cell[metricKey]) : 0
		}
		if (key === 'total') return row[metricKey]
		return row[key]
	}
	const branchSort = useSort(branches, 'total', 'desc', readBranchSort)
	const branchAvgStats = useMemo(() => columnStats(branches.filter(row => row.cheques > 0), 'avg_cheque'), [branches])

	// ---------- Дни недели ----------
	const weekdays = useMemo(() => data?.weekdays || [], [data])
	const weekdayMax = useMemo(() => {
		const key = isRevenue ? 'avg_revenue' : 'avg_cheques'
		let max = 0
		weekdays.forEach(row => Object.values(row.hours || {}).forEach(cell => { if (cell[key] > max) max = cell[key] }))
		return max
	}, [weekdays, isRevenue])
	const weekdayTotalMax = useMemo(() => Math.max(0, ...weekdays.map(row => isRevenue ? row.avg_revenue : row.avg_cheques)), [weekdays, isRevenue])

	// Перетаскивание тепловой карты мышью — по ширине месяца она не помещается
	const heatRef = useRef()
	const drag = useRef({ active: false, x: 0, y: 0, left: 0, top: 0 })
	function dragStart(e) {
		const element = heatRef.current
		if (!element || e.button !== 0) return
		drag.current = { active: true, moved: false, x: e.pageX, y: e.pageY, left: element.scrollLeft, top: element.scrollTop }
	}
	function dragMove(e) {
		const element = heatRef.current
		if (!element || !drag.current.active) return
		const dx = e.pageX - drag.current.x
		const dy = e.pageY - drag.current.y
		if (Math.abs(dx) + Math.abs(dy) > 4) drag.current.moved = true
		element.scrollLeft = drag.current.left - dx
		element.scrollTop = drag.current.top - dy
	}
	function dragEnd() {
		drag.current.active = false
	}

	// Расшифровка: чеки ячейки (день, час, филиал, день недели)
	async function openDetails({ date = null, hour = null, posId = null, weekDay = null, title }) {
		if (drag.current.moved) {
			drag.current.moved = false
			return
		}
		const response = await GET(buildAnalyticsUrl('/chain/report/hours-cheques', applied, {
			date,
			hour,
			pos_id: posId,
			week_days: weekDay ? [weekDay] : applied.week_days,
		}), {}, { loader: true })
		if (httpOk(response)) setDetails({ ...response.data, title })
	}

	function exportExcel() {
		if (!data) return
		const period = `${t('period')}: ${periodText(data.start_date, data.end_date)}`
		const hourColumns = (type) => hours.map(hour => ({ label: formatHour(hour), type, width: type === 'money' ? 12 : 8 }))
		const branchValue = (branch, hour, key) => Number(branch.hours?.[hour]?.[key] || 0)
		const heatSheet = (key, type, label) => ({
			name: `${t('heat_map')} — ${label}`,
			title: `${t('heat_map')} — ${label}`,
			subtitle: period,
			columns: [{ label: t('date'), type: 'text', width: 12 }, { label: t('chain_week_day'), type: 'text', width: 14 }, ...hourColumns(type), { label: t('total'), type }],
			rows: (data.days || []).map(day => {
				const cells = {}
				;(data.heat || []).forEach(cell => { if (cell.date === day.date) cells[cell.hour] = cell[key] })
				return [dayLabel(day.date), weekDayName(day.week_day), ...hours.map(hour => cells[hour] || 0), day[key]]
			}),
			footer: [t('total'), '', ...byHour.map(row => row[key]), totals[key]],
		})
		const branchSheet = (key, type, label) => ({
			name: `${t('chain_branches')} — ${label}`,
			title: `${t('chain_branches_by_hours')} — ${label}`,
			subtitle: period,
			columns: [{ label: t('chain_branch'), type: 'text', width: 28 }, ...hourColumns(type), { label: t('total'), type }, { label: t('peak_hour'), type: 'text', width: 10 }],
			rows: branches.map(branch => [branch.pos_name, ...hours.map(hour => branchValue(branch, hour, key)), branch[key],
				branch.peak_hour !== null ? formatHour(branch.peak_hour) : '']),
			footer: [t('chain_network'), ...byHour.map(row => row[key]), totals[key], ''],
		})
		const weekdaySheet = (key, type, label) => ({
			name: `${t('week_days')} — ${label}`,
			title: `${t('chain_weekday_average')} — ${label}`,
			subtitle: period,
			columns: [{ label: t('chain_week_day'), type: 'text', width: 16 }, { label: t('days'), type: 'int', width: 8 }, ...hourColumns(type === 'money' ? 'money' : 'number'), { label: t('chain_per_day'), type: type === 'money' ? 'money' : 'number' }],
			rows: weekdays.map(row => [weekDayName(row.week_day), row.days, ...hours.map(hour => Number(row.hours?.[hour]?.[`avg_${key}`] || 0)), row[`avg_${key}`]]),
		})

		downloadExcel(`${t('chain_hours_title')} ${periodText(data.start_date, data.end_date)}`, [
			{
				name: t('hours'),
				title: t('chain_hours_title'),
				subtitle: period,
				columns: [
					{ label: t('hours'), type: 'text', width: 10 },
					{ label: t('cheques'), type: 'int', width: 10 },
					{ label: t('share'), type: 'percent', width: 9 },
					{ label: `${t('cheques')} ${t('chain_per_day_lower')}`, type: 'number', width: 12 },
					{ label: t('revenue'), type: 'money' },
					{ label: `${t('revenue')} — ${t('share')}`, type: 'percent', width: 10 },
					{ label: `${t('revenue')} ${t('chain_per_day_lower')}`, type: 'money' },
					{ label: t('avg_cheque'), type: 'money' },
					{ label: `${t('hall')} — ${t('cheques')}`, type: 'int', width: 10 },
					{ label: `${t('takeaway')} — ${t('cheques')}`, type: 'int', width: 10 },
					{ label: `${t('delivery')} — ${t('cheques')}`, type: 'int', width: 10 },
					{ label: `${t('hall')} — ${t('revenue')}`, type: 'money' },
					{ label: `${t('takeaway')} — ${t('revenue')}`, type: 'money' },
					{ label: `${t('delivery')} — ${t('revenue')}`, type: 'money' },
					{ label: t('chain_returns'), type: 'money' },
				],
				rows: byHour.map(row => [
					formatHour(row.hour), row.cheques, row.share_cheques, row.per_day_cheques, row.revenue, row.share_revenue, row.per_day_revenue,
					row.avg_cheque, row.hall, row.takeaway, row.delivery, row.amount_hall, row.amount_takeaway, row.amount_delivery, row.returns,
				]),
				footer: [
					t('total'), totals.cheques, 100, totals.per_day_cheques, totals.revenue, 100, totals.per_day_revenue,
					totals.avg_cheque, totals.hall, totals.takeaway, totals.delivery, totals.amount_hall, totals.amount_takeaway, totals.amount_delivery, totals.returns,
				],
			},
			heatSheet('cheques', 'int', t('cheques')),
			heatSheet('revenue', 'money', t('revenue')),
			branchSheet('cheques', 'int', t('cheques')),
			branchSheet('revenue', 'money', t('revenue')),
			weekdaySheet('cheques', 'number', t('cheques')),
			weekdaySheet('revenue', 'money', t('revenue')),
		])
	}

	function exportDetails() {
		if (!details) return
		downloadExcel(`${t('chain_hours_title')} — ${details.title}`, [{
			name: t('cheques'),
			title: details.title,
			subtitle: `${t('period')}: ${periodText(applied.start_date, applied.end_date)}`,
			columns: [
				{ label: t('cheque'), type: 'text', width: 10 },
				{ label: t('chain_branch'), type: 'text', width: 24 },
				{ label: t('chain_opened_at'), type: 'text', width: 17 },
				{ label: t('chain_closed_at'), type: 'text', width: 17 },
				{ label: t('order_type'), type: 'text', width: 12 },
				{ label: t('source'), type: 'text', width: 16 },
				{ label: t('employee'), type: 'text', width: 16 },
				{ label: t('guests'), type: 'int', width: 8 },
				{ label: t('discount'), type: 'money' },
				{ label: t('amount'), type: 'money' },
			],
			rows: details.rows.map(row => [
				row.cheque_number ? String(row.cheque_number) : String(row.id), row.pos_name,
				formatDate(row.cheque_opened_at, 'dd.MM.yyyy HH:mm'), formatDate(row.cheque_closed_at, 'dd.MM.yyyy HH:mm'),
				orderTypeName(row.order_type), row.source_name || '', row.employee || '', row.guests, row.discount, row.amount,
			]),
			footer: [t('total'), '', '', '', '', '', '', '', '', details.total],
		}])
	}

	const hourRangeLabel = data && data.hour_from !== null
		? `${formatHour(data.hour_from)} – ${String(data.hour_to).padStart(2, '0')}:59`
		: null
	const weekDaysLabel = data && (data.week_days || []).length > 0 ? data.week_days.map(weekDayName).join(', ') : null

	return (
		<>
			<ChainFilterBar
				title={t('chain_hours_title')}
				icon="uil-clock-three"
				subtitle={[hourRangeLabel, weekDaysLabel].filter(Boolean).join(' · ') || null}
				filters={filters}
				wide
				onExcel={exportExcel}
				excelDisabled={!data}
				tabs={[
					{ id: 'heat', label: t('heat_map'), icon: 'uil-apps' },
					{ id: 'hours', label: t('hours'), icon: 'uil-clock' },
					{ id: 'branches', label: t('chain_branches_by_hours'), icon: 'uil-store' },
					{ id: 'weekdays', label: t('chain_weekday_average'), icon: 'uil-calendar-alt' },
				]}
				activeTab={tab}
				onTabChange={setTab}
			>
				<StandardFields filters={filters} options={options} branch={false} />
				<FilterField label={t('chain_hour_from')}>
					<FilterSelect options={hourOptions} allLabel={t('chain_all_hours')} value={draft.start_hour ?? -1}
						onChange={(id) => patch({ start_hour: id === -1 ? null : id })} />
				</FilterField>
				<FilterField label={t('chain_hour_to')}>
					<FilterSelect options={hourToOptions} allLabel={t('chain_all_hours')} value={draft.end_hour ?? -1}
						onChange={(id) => patch({ end_hour: id === -1 ? null : id })} />
				</FilterField>
				<FilterField label={t('week_days')}>
					<FilterSelect isMulti options={weekDayOptions} allLabel={t('chain_all_week_days')} value={draft.week_days} onChange={(ids) => patch({ week_days: ids })} />
				</FilterField>
				<FilterField label={t('chain_metric')}>
					<FilterSegment
						options={[{ id: 'cheques', label: t('cheques'), icon: 'uil-receipt' }, { id: 'revenue', label: t('revenue'), icon: 'uil-money-bill' }]}
						value={metric}
						onChange={setMetric}
					/>
				</FilterField>
			</ChainFilterBar>

			<div className="row g-3 mb-3">
				<div className="col-xl-2 col-md-4 col-6">
					<ChainKpi title={t('cheques')} icon="uil-receipt" color="#5b73e8"
						value={formatMoney(totals.cheques || 0)}
						sub={<>{formatMoney(totals.per_day_cheques || 0)} {t('chain_per_day_lower')}</>} />
				</div>
				<div className="col-xl-2 col-md-4 col-6">
					<ChainKpi title={t('revenue')} icon="uil-money-bill" color="#12b886"
						value={money(totals.revenue)}
						sub={<>{money(totals.per_day_revenue)} {t('chain_per_day_lower')}</>} />
				</div>
				<div className="col-xl-2 col-md-4 col-6">
					<ChainKpi title={t('avg_cheque')} icon="uil-calculator-alt" color="#f59f00" value={money(totals.avg_cheque)}
						sub={Number(totals.returns) > 0 ? <>{t('chain_returns')}: {money(totals.returns)}</> : null} />
				</div>
				<div className="col-xl-2 col-md-4 col-6">
					<ChainKpi title={t('peak_hour')} icon="uil-fire" color="#f06548"
						value={peak.top ? formatHour(peak.top.hour) : '—'}
						sub={peak.top ? <>{formatMetric(peak.top[metricKey])} · {percent(isRevenue ? peak.top.share_revenue : peak.top.share_cheques)}</> : null} />
				</div>
				<div className="col-xl-2 col-md-4 col-6">
					<ChainKpi title={t('chain_quiet_hour')} icon="uil-moon" color="#8791a1"
						value={peak.low ? formatHour(peak.low.hour) : '—'}
						sub={peak.low ? <>{formatMetric(peak.low[metricKey])} · {percent(isRevenue ? peak.low.share_revenue : peak.low.share_cheques)}</> : null} />
				</div>
				<div className="col-xl-2 col-md-4 col-6">
					<ChainKpi title={t('chain_busiest_weekday')} icon="uil-calendar-alt" color="#7c4dff"
						value={busiestWeekday ? weekDayName(busiestWeekday.week_day) : '—'}
						sub={busiestWeekday ? <>{formatMetric(isRevenue ? busiestWeekday.avg_revenue : busiestWeekday.avg_cheques)} {t('chain_per_day_lower')}</> : null} />
				</div>
			</div>

			{tab === 'heat' && (
				<div className="card chain-card mb-3">
					<div className="card-body">
						<div className="chain-toolbar">
							<div className="chain-toolbar-group">
								<h5 className="mb-0 me-2">{t('heat_map')}</h5>
								<span className="chain-muted">{metricLabel} · {t('chain_heat_hint')}</span>
							</div>
							<HeatLegend mode="danger" low={t('low_load')} high={t('high_load')} />
						</div>
						<div className="chain-scroll" ref={heatRef} data-chain-drag="off" style={{ cursor: 'grab' }}
							onMouseDown={dragStart} onMouseMove={dragMove} onMouseUp={dragEnd} onMouseLeave={dragEnd}>
							<table className="chain-table" style={{ fontSize: '12px' }}>
								<thead>
									<tr>
										<th className="chain-sticky" style={{ minWidth: '64px' }}>{t('hours')}</th>
										{(data?.days || []).map(day => (
											<th key={day.date} className="text-center chain-click" style={{ minWidth: '54px', color: day.week_day >= 6 ? '#f06548' : undefined }}
												title={`${weekDayName(day.week_day)} — ${t('chain_open_details')}`}
												onClick={() => openDetails({ date: day.date, title: `${dayLabel(day.date)}, ${weekDayName(day.week_day)}` })}>
												{day.date.slice(8, 10)}.{day.date.slice(5, 7)}
											</th>
										))}
										<th className="text-end chain-total-col" style={{ minWidth: '80px' }}>{t('total')}</th>
									</tr>
								</thead>
								<tbody>
									{!hasData && <ChainEmpty colSpan={(data?.days || []).length + 2} />}
									{hasData && byHour.map(row => (
										<tr key={row.hour}>
											<td className="chain-sticky chain-click" style={{ fontWeight: 600 }}
												onClick={() => openDetails({ hour: row.hour, title: `${formatHour(row.hour)} — ${t('chain_all_days')}` })}>
												{formatHour(row.hour)}
											</td>
											{data.days.map(day => {
												const value = heat.map[`${day.date}|${row.hour}`] || 0
												return (
													<td key={day.date} className={`text-center ${value ? 'chain-click' : ''}`}
														style={{ ...intensityStyle(value, heat.max, 'danger'), padding: '6px 4px', fontWeight: value === heat.max && value > 0 ? 800 : 500 }}
														title={value ? `${dayLabel(day.date)} ${formatHour(row.hour)}: ${formatMetric(value)}` : undefined}
														onClick={() => value && openDetails({ date: day.date, hour: row.hour, title: `${dayLabel(day.date)}, ${formatHour(row.hour)}` })}>
														{formatShort(value)}
													</td>
												)
											})}
											<td className="chain-num chain-total-col">{formatMetric(row[metricKey])}</td>
										</tr>
									))}
								</tbody>
								{hasData && (
									<tfoot>
										<tr>
											<td className="chain-sticky">{t('total')}</td>
											{data.days.map(day => (
												<td key={day.date} className="text-center" style={{ padding: '6px 4px' }}>{formatShort(day[metricKey])}</td>
											))}
											<td className="chain-num chain-total-col">{formatMetric(totals[metricKey])}</td>
										</tr>
									</tfoot>
								)}
							</table>
						</div>
					</div>
				</div>
			)}

			{tab === 'hours' && (
				<div className="row g-3 mb-3">
					<div className="col-xl-6">
						<div className="card chain-card mb-0 h-100">
							<div className="card-body">
								<div className="chain-card-title">
									<h5>{t('chain_by_hours')}</h5>
									<span className="chain-muted">{metricLabel}</span>
								</div>
								<div className="chain-scroll">
									<table className="chain-table">
										<thead>
											<tr>
												<th>{t('hours')}</th>
												<th className="text-end">{t('hall')}</th>
												<th className="text-end">{t('takeaway')}</th>
												<th className="text-end">{t('delivery')}</th>
												<th className="text-end" style={{ minWidth: '130px' }}>{t('total')}</th>
												<th className="text-end">{t('chain_per_day')}</th>
												<th className="text-end">{t('avg_cheque')}</th>
											</tr>
										</thead>
										<tbody>
											{!hasData && <ChainEmpty colSpan={7} />}
											{hasData && byHour.map(row => {
												const isPeak = peak.top && peak.top.hour === row.hour
												return (
													<tr key={row.hour}>
														<td className="chain-click" style={{ fontWeight: isPeak ? 800 : 600 }}
															onClick={() => row.cheques > 0 && openDetails({ hour: row.hour, title: `${formatHour(row.hour)} — ${t('chain_all_days')}` })}>
															{formatHour(row.hour)}
															{isPeak && <i className="uil uil-fire ms-1" style={{ color: '#f06548' }}></i>}
														</td>
														<td className="chain-num">{formatMetric(isRevenue ? row.amount_hall : row.hall)}</td>
														<td className="chain-num">{formatMetric(isRevenue ? row.amount_takeaway : row.takeaway)}</td>
														<td className="chain-num">{formatMetric(isRevenue ? row.amount_delivery : row.delivery)}</td>
														<FillCell percent={maxHourValue > 0 ? row[metricKey] / maxHourValue * 100 : 0} tone="danger">
															<b>{formatMetric(row[metricKey])}</b>
															<div className="chain-muted">{percent(isRevenue ? row.share_revenue : row.share_cheques)}</div>
														</FillCell>
														<td className="chain-num">{formatMetric(isRevenue ? row.per_day_revenue : row.per_day_cheques)}</td>
														<td className="chain-num" style={heatStyle(row.cheques > 0 ? row.avg_cheque : null, hourStats.avg_cheque)}>{row.cheques > 0 ? money(row.avg_cheque) : '—'}</td>
													</tr>
												)
											})}
										</tbody>
										{hasData && (
											<tfoot>
												<tr>
													<td>{t('total')}</td>
													<td className="chain-num">{formatMetric(isRevenue ? totals.amount_hall : totals.hall)}</td>
													<td className="chain-num">{formatMetric(isRevenue ? totals.amount_takeaway : totals.takeaway)}</td>
													<td className="chain-num">{formatMetric(isRevenue ? totals.amount_delivery : totals.delivery)}</td>
													<td className="chain-num">{formatMetric(totals[metricKey])}</td>
													<td className="chain-num">{formatMetric(isRevenue ? totals.per_day_revenue : totals.per_day_cheques)}</td>
													<td className="chain-num">{money(totals.avg_cheque)}</td>
												</tr>
											</tfoot>
										)}
									</table>
								</div>
							</div>
						</div>
					</div>
					<div className="col-xl-6">
						<ChannelsChart rows={byHour} isRevenue={isRevenue} mode={chartMode} onMode={setChartMode} />
					</div>
				</div>
			)}

			{tab === 'branches' && (
				<div className="card chain-card mb-3">
					<div className="card-body">
						<div className="chain-toolbar">
							<div className="chain-toolbar-group">
								<h5 className="mb-0 me-2">{t('chain_branches_by_hours')}</h5>
								<span className="chain-muted">{metricLabel} · {t('chain_branch_peak_hint')}</span>
							</div>
							<HeatLegend mode="danger" low={t('low_load')} high={t('high_load')} />
						</div>
						<div className="chain-scroll">
							<table className="chain-table" style={{ fontSize: '12px' }}>
								<thead>
									<tr>
										<th className="chain-index chain-sticky">#</th>
										<SortTh sortKey="pos_name" sort={branchSort.sort} onSort={branchSort.toggle} className="chain-sticky-2">{t('chain_branch')}</SortTh>
										{hours.map(hour => (
											<SortTh key={hour} sortKey={`h:${hour}`} sort={branchSort.sort} onSort={branchSort.toggle} className="text-center" style={{ minWidth: '58px' }}>
												{String(hour).padStart(2, '0')}
											</SortTh>
										))}
										<SortTh sortKey="total" sort={branchSort.sort} onSort={branchSort.toggle} className="text-end chain-total-col">{t('total')}</SortTh>
										<SortTh sortKey="peak_hour_cheques" sort={branchSort.sort} onSort={branchSort.toggle} className="text-end">{t('peak_hour')}</SortTh>
										<SortTh sortKey="avg_cheque" sort={branchSort.sort} onSort={branchSort.toggle} className="text-end">{t('avg_cheque')}</SortTh>
									</tr>
								</thead>
								<tbody>
									{branchSort.sorted.length === 0 && <ChainEmpty colSpan={hours.length + 5} />}
									{branchSort.sorted.map((branch, rowIndex) => {
										const values = hours.map(hour => Number(branch.hours?.[hour]?.[metricKey] || 0))
										const rowMax = Math.max(0, ...values)
										const colorIndex = branches.indexOf(branch)
										return (
											<tr key={branch.pos_id}>
												<td className="chain-index chain-sticky">{rowIndex + 1}</td>
												<td className="chain-name chain-sticky-2">
													<span className="d-inline-flex align-items-center">
														<span className="chain-dot" style={{ background: chainColor(colorIndex) }}></span>
														{branch.pos_name}
													</span>
												</td>
												{hours.map((hour, index) => {
													const value = values[index]
													const isMax = value > 0 && value === rowMax
													return (
														<td key={hour} className={`text-center ${value ? 'chain-click' : ''}`}
															style={{ ...intensityStyle(value, rowMax, 'danger'), padding: '6px 4px', fontWeight: isMax ? 800 : 500 }}
															title={value ? `${branch.pos_name}, ${formatHour(hour)}: ${formatMetric(value)}` : undefined}
															onClick={() => value && openDetails({ hour, posId: branch.pos_id, title: `${branch.pos_name}, ${formatHour(hour)}` })}>
															{formatShort(value)}
														</td>
													)
												})}
												<td className="chain-num chain-total-col">{formatMetric(branch[metricKey])}</td>
												<td className="chain-num">
													{branch.peak_hour !== null ? <b>{formatHour(branch.peak_hour)}</b> : '—'}
													{branch.peak_hour !== null && <div className="chain-muted">{formatMoney(branch.peak_hour_cheques)} {t('chain_cheques_lower')}</div>}
												</td>
												<td className="chain-num" style={heatStyle(branch.cheques > 0 ? branch.avg_cheque : null, branchAvgStats)}>{branch.cheques > 0 ? money(branch.avg_cheque) : '—'}</td>
											</tr>
										)
									})}
								</tbody>
								{branchSort.sorted.length > 0 && (
									<tfoot>
										<tr>
											<td className="chain-sticky"></td>
											<td className="chain-sticky-2">{t('chain_network')}</td>
											{byHour.map(row => (
												<td key={row.hour} className="text-center" style={{ ...intensityStyle(row[metricKey], maxHourValue, 'danger'), padding: '6px 4px' }}>
													{formatShort(row[metricKey])}
												</td>
											))}
											<td className="chain-num chain-total-col">{formatMetric(totals[metricKey])}</td>
											<td className="chain-num">{totals.peak_hour !== null && totals.peak_hour !== undefined ? formatHour(totals.peak_hour) : '—'}</td>
											<td className="chain-num">{money(totals.avg_cheque)}</td>
										</tr>
									</tfoot>
								)}
							</table>
						</div>
						<div className="chain-muted mt-2">
							<i className="uil uil-info-circle me-1"></i>
							{t('chain_branches_hours_hint')}
						</div>
					</div>
				</div>
			)}

			{tab === 'weekdays' && (
				<div className="card chain-card mb-3">
					<div className="card-body">
						<div className="chain-toolbar">
							<div className="chain-toolbar-group">
								<h5 className="mb-0 me-2">{t('chain_weekday_average')}</h5>
								<span className="chain-muted">{metricLabel} · {t('chain_weekday_hint')}</span>
							</div>
							<HeatLegend mode="danger" low={t('low_load')} high={t('high_load')} />
						</div>
						<div className="chain-scroll">
							<table className="chain-table" style={{ fontSize: '12px' }}>
								<thead>
									<tr>
										<th className="chain-sticky" style={{ minWidth: '130px' }}>{t('chain_week_day')}</th>
										{hours.map(hour => (
											<th key={hour} className="text-center" style={{ minWidth: '58px' }}>{String(hour).padStart(2, '0')}</th>
										))}
										<th className="text-end" style={{ minWidth: '120px' }}>{t('chain_per_day')}</th>
										<th className="text-end">{t('avg_cheque')}</th>
									</tr>
								</thead>
								<tbody>
									{weekdays.length === 0 && <ChainEmpty colSpan={hours.length + 3} />}
									{weekdays.map(row => (
										<tr key={row.week_day}>
											<td className="chain-sticky" style={{ fontWeight: 600, color: row.week_day >= 6 ? '#f06548' : undefined }}>
												{weekDayName(row.week_day)}
												<div className="chain-muted">{row.days} {t('chain_days_lower')}</div>
											</td>
											{hours.map(hour => {
												const cell = row.hours?.[hour]
												const value = cell ? Number(isRevenue ? cell.avg_revenue : cell.avg_cheques) : 0
												return (
													<td key={hour} className={`text-center ${value ? 'chain-click' : ''}`}
														style={{ ...intensityStyle(value, weekdayMax, 'danger'), padding: '6px 4px', fontWeight: value === weekdayMax && value > 0 ? 800 : 500 }}
														title={value ? `${weekDayName(row.week_day)}, ${formatHour(hour)}: ${formatMetric(value)} ${t('chain_on_average')}` : undefined}
														onClick={() => value && openDetails({ hour, weekDay: row.week_day, title: `${weekDayName(row.week_day)}, ${formatHour(hour)}` })}>
														{formatShort(value)}
													</td>
												)
											})}
											<FillCell percent={weekdayTotalMax > 0 ? (isRevenue ? row.avg_revenue : row.avg_cheques) / weekdayTotalMax * 100 : 0} tone="danger">
												<b>{formatMetric(isRevenue ? row.avg_revenue : row.avg_cheques)}</b>
											</FillCell>
											<td className="chain-num">{money(row.avg_cheque)}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				</div>
			)}

			{/* MODAL CHEQUES */}
			<Modal show={!!details} onHide={() => setDetails(null)} size="xl" centered scrollable>
				<Modal.Header closeButton>
					<Modal.Title>
						{details?.title}
						<div className="chain-muted fw-normal">{periodText(applied.start_date, applied.end_date)} · {formatMoney(details?.rows?.length || 0)} {t('chain_cheques_lower')} · {money(details?.total)}</div>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ height: '520px' }}>
					{details?.limited && (
						<div className="chain-note mb-2"><i className="uil uil-exclamation-triangle"></i>{t('chain_cheques_limited')}</div>
					)}
					<table className="chain-table">
						<thead>
							<tr>
								<th>#</th>
								<th>{t('cheque')}</th>
								<th>{t('chain_branch')}</th>
								<th>{t('chain_opened_at')}</th>
								<th>{t('chain_closed_at')}</th>
								<th>{t('order_type')}</th>
								<th>{t('source')}</th>
								<th>{t('employee')}</th>
								<th className="text-end">{t('guests')}</th>
								<th className="text-end">{t('discount')}</th>
								<th className="text-end">{t('amount')}</th>
							</tr>
						</thead>
						<tbody>
							{(details?.rows || []).length === 0 && <ChainEmpty colSpan={11} />}
							{(details?.rows || []).map((row, index) => (
								<tr key={row.id}>
									<td className="chain-index">{index + 1}</td>
									<td>{row.cheque_number || row.id}</td>
									<td>{row.pos_name}</td>
									<td className="text-nowrap">{formatDate(row.cheque_opened_at, 'dd.MM.yy HH:mm')}</td>
									<td className="text-nowrap">{formatDate(row.cheque_closed_at, 'dd.MM.yy HH:mm')}</td>
									<td>{orderTypeName(row.order_type)}</td>
									<td>{row.source_name || '—'}</td>
									<td>{row.employee || '—'}</td>
									<td className="chain-num">{formatMoney(row.guests)}</td>
									<td className="chain-num">{Number(row.discount) > 0 ? money(row.discount) : '—'}</td>
									<td className="chain-num"><b>{money(row.amount)}</b></td>
								</tr>
							))}
						</tbody>
					</table>
				</Modal.Body>
				<Modal.Footer>
					<ExcelButton onClick={exportDetails} disabled={!details?.rows?.length} />
					<button type="button" className="btn btn-outline-secondary" onClick={() => setDetails(null)}>{t('close')}</button>
				</Modal.Footer>
			</Modal>
		</>
	)
}

// Каналы по часам: зал / с собой / доставка — в штуках (суммах) или долями часа
function ChannelsChart({ rows, isRevenue, mode, onMode }) {
	const { t } = useTranslation()
	const value = (row, channel) => isRevenue ? Math.round(row[`amount_${channel}`]) : row[channel]
	const series = [
		{ name: t('hall'), data: rows.map(row => value(row, 'hall')) },
		{ name: t('takeaway'), data: rows.map(row => value(row, 'takeaway')) },
		{ name: t('delivery'), data: rows.map(row => value(row, 'delivery')) },
	]
	const options = {
		chart: { type: 'bar', stacked: true, stackType: mode === 'share' ? '100%' : undefined, toolbar: { show: false } },
		plotOptions: { bar: { columnWidth: '70%', borderRadius: 2 } },
		colors: [CHANNEL_COLORS.hall, CHANNEL_COLORS.takeaway, CHANNEL_COLORS.delivery],
		dataLabels: { enabled: false },
		legend: { position: 'bottom', fontSize: '12px' },
		grid: { borderColor: '#eef2f6', strokeDashArray: 3 },
		xaxis: { categories: rows.map(row => String(row.hour).padStart(2, '0')) },
		yaxis: {
			labels: {
				formatter: number => mode === 'share' ? `${Math.round(number)}%` : isRevenue ? shortMoney(number) : formatMoney(Math.round(number)),
			},
		},
		tooltip: { shared: true, y: { formatter: number => isRevenue ? money(number) : formatMoney(number) } },
	}

	return (
		<div className="card chain-card mb-0 h-100">
			<div className="card-body">
				<div className="chain-card-title">
					<h5>{t('chain_channels')}</h5>
					<FilterSegment
						options={[{ id: 'absolute', label: isRevenue ? t('revenue') : t('cheques') }, { id: 'share', label: t('share') }]}
						value={mode}
						onChange={onMode}
					/>
				</div>
				<ReactApexChart key={`${mode}-${isRevenue}`} type="bar" height={420} series={series} options={options} />
			</div>
		</div>
	)
}

export default Hours
